import EnrollmentCardsSection from 'components/common/enrollment/EnrollmentCardsSection';
import ProposalsTable from 'components/common/proposal/ProposalsTable';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useRootStore } from 'store/rootStore';
import { toJS } from 'mobx';
import ChangeEffectiveDateModal from 'components/group-info/ChangeEffectiveDateModal';
import { EnrollmentPeriod, EnrollmentType } from 'types/enrollmentService';
import { DynamicEnrollmentCard, INITIAL_CURRENT_PERIOD } from 'store/ui-stores/group-info/enrollmentCardStore';
import { createStyles } from './styles';

const GroupProfileContent = observer((): JSX.Element => {
  const { groupId } = useParams<{ groupId: string }>();

  const {
    enrollmentCardStore: {
      loading,
      activePeriods,
      inactivePeriods,
      currentPeriod,
      showChangeEffectiveDateModal,
      collectEnrollmentPeriods,
      resetInitialData,
      updateChangeEffectiveDateModalStatus,
    },
    groupProposalsTableStore: { resetProposalsData },
  } = useRootStore();

  // React effects
  useEffect(() => {
    void collectEnrollmentPeriods(groupId);
    return (): void => {
      resetInitialData();
      resetProposalsData();
    };
  }, []);

  const inactiveEnrollmentPeriods = inactivePeriods.map((period) => toJS(period)).slice();
  const activeEnrollmentPeriods = activePeriods.map((period) => toJS(period)).slice();
  const convertedCurrentPeriod = toJS(currentPeriod);

  // Find and show only enrollment periods that in enrolling stage
  const activeNewSaleEnrollmentPeriods = activeEnrollmentPeriods
    .filter((enrollmentPeriod) => enrollmentPeriod.hasConfirmedProposal)
    .filter(
      (enrollmentPeriod) =>
        enrollmentPeriod.type === EnrollmentType.NEW_SALE || enrollmentPeriod.type === EnrollmentType.RESUBMIT_NEW_SALE,
    );

  // Find and show all renewal enrollment periods
  const activeRenewalEnrollmentPeriods = activeEnrollmentPeriods.filter(
    (enrollmentPeriod) =>
      enrollmentPeriod.type === EnrollmentType.RENEWAL || enrollmentPeriod.type === EnrollmentType.INSTALLED,
  );

  const getUnavailableEffectiveDates = (): string[] => {
    const filterPeriods = (periodsList: EnrollmentPeriod[] | DynamicEnrollmentCard[]): string[] => {
      return periodsList
        .filter((period) => period.id !== convertedCurrentPeriod.periodId)
        .map((period) => period.effectiveDate);
    };

    return [...filterPeriods(activeEnrollmentPeriods), ...filterPeriods(inactiveEnrollmentPeriods)];
  };

  const classes = createStyles();
  return (
    <>
      {activeNewSaleEnrollmentPeriods.length > 0 && (
        <EnrollmentCardsSection
          cardsList={activeNewSaleEnrollmentPeriods}
          loading={loading}
          sectionTitle="Current enrollments"
        />
      )}
      {activeRenewalEnrollmentPeriods.length > 0 && (
        <EnrollmentCardsSection
          cardsList={activeRenewalEnrollmentPeriods}
          loading={loading}
          sectionTitle="Active enrollments"
        />
      )}
      <div>
        <section className={classes.section}>
          <h3 className={classes.sectionTitle}>Proposals</h3>
          <ProposalsTable />
        </section>
        {inactiveEnrollmentPeriods && inactiveEnrollmentPeriods.length > 0 && (
          <EnrollmentCardsSection
            type="archived"
            cardsList={inactiveEnrollmentPeriods}
            sectionTitle="Previous Policy Information"
            sectionSubtitle="You will only see previous policies that were processed through InsuranceMenu"
            className={classes.inactiveEnrollmentsSection}
          />
        )}
      </div>
      <ChangeEffectiveDateModal
        show={showChangeEffectiveDateModal}
        currentPeriod={convertedCurrentPeriod}
        updateEnrollments={async (): Promise<void> => {
          await collectEnrollmentPeriods(groupId);
        }}
        onClose={(): void => {
          updateChangeEffectiveDateModalStatus(false, INITIAL_CURRENT_PERIOD);
        }}
        unavailableEffectiveDates={getUnavailableEffectiveDates()}
      />
    </>
  );
});

export default GroupProfileContent;
